import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * Keeps a flat set of string values in the URL query string (page, filters, sort), so views are
 * shareable and survive reloads and the back button. Values equal to their default are omitted.
 */
export function useUrlState<T extends Record<string, string>>(defaults: T): [T, (patch: Partial<T>) => void] {
  const location = useLocation();
  const history = useHistory();

  const state = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const next: Record<string, string> = {};
    Object.keys(defaults).forEach((key) => {
      const value = params.get(key);
      next[key] = value === null ? defaults[key] : value;
    });
    return next as T;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const update = useCallback(
    (patch: Partial<T>) => {
      const params = new URLSearchParams(history.location.search);
      Object.keys(patch).forEach((key) => {
        const value = patch[key];
        if (value === undefined || value === null || value === '' || value === defaults[key]) params.delete(key);
        else params.set(key, String(value));
      });
      const search = params.toString();
      history.replace({ pathname: history.location.pathname, search: search ? `?${search}` : '' });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history],
  );

  return [state, update];
}
